// C:\Users\USER\bookfete\frontend\src\components\layout\HeaderLuxe.js
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { checkIsAdmin } from '../../services/adminApi';
import LanguageSwitcher from '../common/LanguageSwitcher';
import CountrySwitcher from '../common/CountrySwitcher';
import '../../styles/luxe-theme.css';
import './HeaderLuxe.css';

const IconGear = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.757.426 1.757 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.757-2.924 1.757-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.757-.426-1.757-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const IconChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBurger = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    {open ? (
      <>
        <path d="M5 5 L15 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M15 5 L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </>
    ) : (
      <>
        <path d="M3 6 H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M3 10 H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M3 14 H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </>
    )}
  </svg>
);

// Sur un ecran etroit, la navigation se replie derriere un bouton (voir
// HeaderLuxe.css). Elle etait auparavant entierement en styles inline, donc
// impossible a adapter : elle debordait de 189 px sur un telephone, ce qui
// poussait "Deconnexion" hors de l'ecran et creait un defilement lateral sur
// TOUTES les pages du site (mesure le 2026-09-14).
const HeaderLuxe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const userMenuRef = React.useRef(null);
  // Largeur au-dela de laquelle la navigation tient sur une ligne — meme
  // seuil que la regle @media, a garder synchronise. En dessous, le menu est
  // masque tant qu'on ne l'ouvre pas ; au-dessus il doit rester visible en
  // permanence, sans dependre de l'etat d'ouverture.
  const [compact, setCompact] = React.useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1200px)').matches
  ));

  React.useEffect(() => {
    const media = window.matchMedia('(max-width: 1200px)');
    const apply = (event) => {
      setCompact(event.matches);
      if (!event.matches) setMenuOpen(false); // repasse en large : plus de menu ouvert qui traine
    };
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Le serveur seul sait qui est administrateur (ADMIN_EMAILS n'existe pas
  // cote client, et c'est voulu). Recalcule a chaque changement de session :
  // se deconnecter doit faire disparaitre le lien immediatement.
  React.useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    let cancelled = false;
    checkIsAdmin().then(({ isAdmin }) => { if (!cancelled) setIsAdmin(isAdmin); });
    return () => { cancelled = true; };
  }, [user]);

  // Naviguer referme le menu : sinon il resterait ouvert par-dessus la page
  // qu'on vient de demander.
  React.useEffect(() => { setMenuOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKey = (event) => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  // Menu avatar (retour utilisateur sur la maquette "Maquettes Bookipix",
  // 2026-10-11) : se ferme sur Echap ou sur un clic en dehors, comme tout
  // menu deroulant standard.
  React.useEffect(() => {
    if (!userMenuOpen) return undefined;
    const handleKey = (event) => { if (event.key === 'Escape') setUserMenuOpen(false); };
    const handleClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setUserMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    await supabase.auth.signOut();
    navigate('/');
  };

  // Les 4 pages publiques toujours affichees (retour utilisateur 2026-10-11 :
  // "mets le menu du haut comme sur la capture" — reprend la maquette de
  // l'artefact Exemples) : separees du reste (compte/langue/pays/deconnexion,
  // voir .site-nav-utility) pour garder cette meme lisibilite simple, sans
  // retirer aucune fonction existante — seulement regroupee differemment.
  const PUBLIC_LINKS = [
    { to: '/', label: t('header.home') },
    { to: '/how-it-works', label: t('header.howItWorks') },
    { to: '/exemples', label: t('header.examples') },
    { to: '/tarifs', label: t('header.pricing') }
  ];

  return (
    <header className="site-header">
      <div className="container-luxe site-header-inner">
        <Link to="/" className="site-header-logo">
          <span className="site-header-mark" aria-hidden="true" />
          Bookipix
        </Link>

        <button
          type="button"
          className="site-header-burger"
          onClick={() => setMenuOpen((previous) => !previous)}
          aria-expanded={menuOpen}
          aria-controls="site-nav"
          aria-label={menuOpen ? t('header.closeMenu') : t('header.openMenu')}
        >
          <IconBurger open={menuOpen} />
        </button>

        {/* `hidden` seulement en mode compact : en large, la navigation est
            toujours la, quel que soit l'etat du menu. */}
        <div id="site-nav" className="site-nav-wrap" hidden={compact && !menuOpen}>
          <nav className="site-nav-public" aria-label={t('header.mainNav')}>
            {PUBLIC_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="site-nav-link"
                aria-current={location.pathname === link.to ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <nav className="site-nav-utility">
          {user ? (
            <>
              <Link to="/dashboard" className="site-nav-link">
                {t('header.dashboard')}
              </Link>
              <LanguageSwitcher />
              {/* Menu avatar (maquette "Maquettes Bookipix", 2026-10-11) :
                  remplace la ligne surchargee compte/admin/pays/deconnexion
                  par un seul bouton. Une session ANONYME n'a pas d'email
                  (composition autorisee avant creation de compte, voir
                  services/anonymousSession.js) : on le dit franchement dans
                  le bloc "who" plutot que de laisser un vide, parce que
                  c'est justement l'etat ou il faut penser a s'inscrire. */}
              <div className="site-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className="site-user-menu-trigger"
                  onClick={() => setUserMenuOpen((previous) => !previous)}
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen}
                  aria-label={t('header.accountMenu')}
                >
                  <span className="site-user-menu-avatar" aria-hidden="true">
                    {user.is_anonymous ? '?' : (user.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <IconChevronDown />
                </button>
                <div className="site-user-menu-panel" hidden={!userMenuOpen}>
                  <div className={`site-user-menu-who${user.is_anonymous ? ' is-anonyme' : ''}`}>
                    <strong title={user.email || t('header.noAccountTitle')}>
                      {user.email || t('header.noAccount')}
                    </strong>
                  </div>
                  <Link to="/dashboard" className="site-user-menu-item">{t('header.dashboard')}</Link>
                  <Link to="/account" className="site-user-menu-item">
                    <IconGear />
                    {t('header.accountSpace')}
                  </Link>
                  {/* Lien vers l'espace d'administration, affiche UNIQUEMENT
                      aux administrateurs (le serveur repond oui/non, voir
                      adminApi.checkIsAdmin) — pas une protection, juste de
                      quoi ne pas avoir a retenir l'URL. Reste volontairement
                      en francais : seul le fondateur le voit jamais. */}
                  {isAdmin && (
                    <Link to="/admin" className="site-user-menu-item is-admin" title="Espace d'administration">
                      Administration
                    </Link>
                  )}
                  <div className="site-user-menu-row">
                    <span>{t('country.switchLabel')}</span>
                    <CountrySwitcher />
                  </div>
                  <button type="button" onClick={handleLogout} className="site-user-menu-item is-logout">
                    {t('header.logout')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              <Link to="/login" className="btn btn-outline">
                {t('header.login')}
              </Link>
              {/* VERS LA CREATION, PAS VERS L INSCRIPTION (2026-09-20).
                  Ce bouton est la premiere promesse du produit : composer
                  son livre sans rien demander. Il ouvrait un formulaire
                  d'inscription — l'inverse exact de ce que l'application
                  sait faire depuis le demarrage sans compte. Les liens de
                  la page d accueil, eux, pointaient deja au bon endroit. */}
              <Link to="/create-book" className="btn btn-primary">
                {t('header.createBook')}
              </Link>
            </>
          )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default HeaderLuxe;
